"use client";

import { Fragment, useState, type ReactNode } from "react";
import { Button } from "@tripcart/traveler-ui/components/button";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@tripcart/traveler-ui/components/toggle-group";

import { useTranslator } from "@/lib/translation-context";

import { RailDots } from "../_rail-dots.client";
import { useIsDesktop } from "../_use-is-desktop.client";

import styles from "./source-filter.module.css";

import { pageReviews, type ReviewGroup } from "./paging";
import {
  PlatformMark,
  platformLabel,
  type PlatformStats,
} from "./rating-summary";

export interface FilterableReview {
  id: string;
  platform: string;
  /** The card, rendered on the SERVER and passed through as an element. React
   *  elements cross the boundary as plain data, so the card's markup ships no
   *  JS — only this wrapper's filter state does. */
  node: ReactNode;
}

/**
 * The guide's `.rv-fb` — filter the written reviews by where they came from.
 *
 * RENDERS NO FILTER when only one platform is connected, and the prototype's
 * own comment is the reason: "one connected platform means one source, so the
 * filter has no job". A control whose every option returns the same list is
 * worse than no control — it invites a click and answers with nothing. The
 * cards still render; only the chips disappear.
 *
 * Filtering is client state rather than a re-fetch: the reviews are already on
 * the page, so re-requesting them to hide rows would be slower and would lose
 * the reader's scroll position.
 */
interface ReviewsWithSourceFilterProps {
  platforms: PlatformStats[];
  reviews: FilterableReview[];
  /** Shown when a source is selected that has no reviews on THIS tour — the
   *  agency has Google reviews, just not for this trip. Distinct from having no
   *  reviews at all, which the caller handles. */
  emptyForSource: ReactNode;
}

export function ReviewsWithSourceFilter({
  platforms,
  reviews,
  emptyForSource,
}: ReviewsWithSourceFilterProps) {
  const t = useTranslator();
  const isDesktop = useIsDesktop();
  const [source, setSource] = useState<string | null>(null);
  // Presses of "Load more", plus one — NOT a row count. The flat and grouped
  // shapes reveal different units, and which one is on screen is known only
  // after hydration; `pageReviews` explains why a press count is the state that
  // means the same thing to both.
  const [pages, setPages] = useState(1);
  const showFilter = platforms.length > 1;

  const visible = source
    ? reviews.filter((r) => r.platform === source)
    : reviews;
  // How many sources the VISIBLE reviews actually come from. One means there
  // is nothing to name — a heading over the only group is a label for the
  // section, which the section already has.
  const groupCount = new Set(visible.map((r) => r.platform)).size;
  // Rails only below `lg`, and only when there is more than one source to
  // separate. Everything else is the flat newest-first column.
  const grouped = !isDesktop && groupCount > 1;
  const { flat, groups, remaining, nextPress } = pageReviews(
    visible,
    platforms,
    grouped,
    pages,
  );

  // Switching source restarts the paging. Carrying the old count over meant
  // picking a source with three reviews after scrolling deep into "all" showed
  // the list already "fully loaded", and picking a busier one showed rows the
  // reader never asked to expand.
  const selectSource = (next: string | null): void => {
    setSource(next);
    setPages(1);
  };

  return (
    <>
      {showFilter ? (
        <SourceChips
          platforms={platforms}
          source={source}
          onSelect={selectSource}
          allLabel={t("All sources")}
        />
      ) : null}

      {visible.length > 0 ? (
        <>
          <ReviewList
            flat={flat}
            groups={groups}
            grouped={grouped}
            showHeadings={groupCount > 1}
          />

          {remaining > 0 ? (
            // The badge shows what ONE PRESS reveals, not everything held
            // back. On a phone those differ: four sources leave 68 in reserve
            // while a press deepens each rail by three, so a badge reading 68
            // promised one press for what takes six.
            <LoadMore
              remaining={nextPress}
              onLoadMore={() => setPages(pages + 1)}
              label={t("Load more reviews")}
            />
          ) : null}
        </>
      ) : (
        emptyForSource
      )}
    </>
  );
}

/**
 * The reviews themselves — the one place the desktop and phone shapes differ.
 *
 * DESKTOP is the guide's `.rv-list`: ONE column, IN THE ORDER THE PAYLOAD SENDS
 * THEM, which is newest first across every source. The MOBILE guide reads the
 * same reviews platform by platform (`.rvg`), each source its own named rail.
 *
 * Two branches, not one tree bent by CSS. An earlier pass built the groups
 * always and dissolved them at `lg` with `display: contents` — but `contents`
 * drops the BOX, not the DOM order, so desktop silently changed from
 * newest-first to all-Google-then-all-Trustpilot. It was worse with "Load
 * more": the extra six are appended to the flat list and then re-bucketed, so a
 * new Google review lands ABOVE Trustpilot rows the reader has already read and
 * the list appears to shuffle under them.
 *
 * THE COST, stated plainly: a desktop visitor's first HTML is the phone shape
 * and swaps on hydration (see `useIsDesktop`). The two branches use different
 * element types under different parents, so React does not patch them — it
 * unmounts every card and rebuilds it, and the section's height changes from a
 * few ~300px rails to one full-width column.
 *
 * That is paid for by this section sitting far below the fold AND behind
 * `content-visibility: auto`, so on any normal load hydration finishes long
 * before it is reached and nothing observable moves. It is NOT free in the
 * worst case: a reader on a slow desktop connection who has already scrolled
 * into Reviews will see the list re-flow under them. Do not copy this pattern
 * above the fold, and if this section ever moves up the page, revisit it.
 */
function ReviewList({
  flat,
  groups,
  grouped,
  showHeadings,
}: {
  flat: FilterableReview[];
  groups: ReviewGroup<FilterableReview>[];
  grouped: boolean;
  showHeadings: boolean;
}) {
  return (
    <div className={styles.list}>
      {grouped
        ? groups.map((group) => (
            <ReviewRail
              key={group.platform}
              platform={group.platform}
              reviews={group.reviews}
              showHeading={showHeadings}
            />
          ))
        : flat.map((r) => <Fragment key={r.id}>{r.node}</Fragment>)}
    </div>
  );
}

/** The "All sources" chip's value. Not a platform id, and not "" — an empty
 *  value is never reported as a choice. */
const ALL_SOURCES = "*";

/** The chip row — "All sources" plus one chip per connected platform. Rendered
 *  only when there is more than one; see the component docstring above. */
function SourceChips({
  platforms,
  source,
  onSelect,
  allLabel,
}: {
  platforms: PlatformStats[];
  source: string | null;
  onSelect: (next: string | null) => void;
  allLabel: string;
}) {
  const t = useTranslator();
  return (
    // 10px is the guide's `.fb` gap BETWEEN chips. It was 7px,
    // which is the 7px gap `.rv-fb .fb-chip` sets INSIDE a chip — so the space
    // separating two sources matched the space between a source's mark and its
    // own label, and the row read as one run-on control. `.rv-fb` overrides
    // `.fb`'s padding, margin and background but deliberately leaves its gap.
    //
    // On a phone it is the guide's `.fchips` — ONE row that slides. Five source
    // chips wrap to three rows at 358px, which puts more chrome than reviews on
    // the first screen of the section. From `lg` there is room to wrap.
    //
    // The bleed tracks `SITE_CONTAINER`'s own gutter, which steps from 16px
    // to 32px at `md`; a flat 16px bleed left the row 16px short of each edge
    // between 768px and 1023px, so its bottom rule stopped before the screen
    // did.
    <ToggleGroup
      variant="chip"
      value={source ?? ALL_SOURCES}
      onValueChange={(next) => onSelect(next === ALL_SOURCES ? null : next)}
      aria-label={t("Filter reviews by source")}
      className={styles.chips}
    >
      <ToggleGroupItem value={ALL_SOURCES} className={styles.chip}>
        {allLabel}
      </ToggleGroupItem>
      {platforms.map((p) => (
        <ToggleGroupItem
          key={p.platform}
          value={p.platform}
          className={styles.chip}
        >
          {/* The guide gives every source chip its platform disc — that is
              what `.rv-fb .fb-chip .plat-m` exists to resize. The same mark
              the summary panel and every review card already draw, so a
              reader matches a filter to the badge on a card by colour rather
              than by re-reading the name. "All sources" has no platform and
              so has no mark. */}
          <PlatformMark platform={p.platform} size="chip" />
          {platformLabel(p.platform)}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}

/** Guide `.rv-more`. The remaining count sits ON the button rather than being
 *  implied by it — a bare "Load more" hides whether two are left or two
 *  hundred. The number is how many more reviews this page holds, which is the
 *  only figure it can state without guessing at the operator's total. */
function LoadMore({
  remaining,
  onLoadMore,
  label,
}: {
  remaining: number;
  onLoadMore: () => void;
  label: string;
}) {
  return (
    <div className={styles.more}>
      <Button variant="secondary" onClick={onLoadMore}>
        {label}
        <span className={styles.moreCount}>{remaining}</span>
      </Button>
    </div>
  );
}

/**
 * One source's reviews: the mobile guide's `.rvg` — a named header, then a rail
 * that runs off the right edge.
 *
 * 84% with a 300px cap is the guide's own `flex: 0 0 min(300px, 84%)`,
 * and the 84% is doing real work: it guarantees the NEXT card is visible at any
 * phone width, which is the only thing that tells a reader the rail scrolls.
 *
 * The peek says "there is more"; the guide's `.rvc-dots` under the rail says
 * WHERE, which the peek cannot — a reader who lands mid-rail sees a card
 * hanging off each edge either way. They are drawn only on a rail of more than
 * one card, exactly as the guide gates them (`list.length > 1`).
 *
 * No scroll-snap. A snap point inside a rail that carries its own side padding
 * scrolls that padding out of view on load and clips the first card; the guide
 * does not snap this rail either.
 *
 * BELOW `lg` ONLY — the caller renders a flat column above it, so nothing here
 * needs a desktop escape hatch.
 */
function ReviewRail({
  platform,
  reviews,
  showHeading,
}: {
  platform: string;
  reviews: FilterableReview[];
  showHeading: boolean;
}) {
  // Per-platform, so two rails on one page never share a track id — the dots
  // find their rail by id, and a duplicate would point both rows at the first.
  const trackId = `tour-reviews-rail-${platform}`;
  return (
    // `min-width: 0`: this is a grid item, and a grid item's default
    // `min-width: auto` lets its content set a floor — the rail's negative
    // margin pushed the item 121px past its track, which carried the heading's
    // right-aligned count off the screen with it.
    <section className={styles.rail}>
      {showHeading ? (
        <h3 className={styles.railHeading}>
          <PlatformMark platform={platform} />
          <span className={styles.railName}>{platformLabel(platform)}</span>
          <span className={styles.railCount}>{reviews.length}</span>
        </h3>
      ) : null}
      {/* The negative margin has to MATCH THE CONTAINER'S GUTTER, and
          `SITE_CONTAINER` widens from 16px to 32px at `md`. At a flat 16px
          the rail bled only half the gutter between 768px and 1023px — cards
          stopping 16px short of each edge read as broken alignment rather than
          as a rail that runs off the screen. */}
      <div id={trackId} className={styles.track}>
        {reviews.map((r) => (
          <div key={r.id} className={styles.slide}>
            {r.node}
          </div>
        ))}
      </div>
      {/* Guide `.rvc-dots` — 9px under the rail. */}
      <RailDots
        trackId={trackId}
        total={reviews.length}
        className={styles.dots}
      />
    </section>
  );
}
